/**
 * 打卡浮层三小件(无壳,供 ClockActionRow 的 .nvc-mini 浮层与 FlyoutCard 原地子视图复用)。
 * 像素/结构真相源:handoff core.jsx BreakPicker(132-143)/WorkTypePicker(145-162)
 * + variants.jsx VariantC confirm 段(120-127),类名 nvc- 前缀。
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronLeft,
  Coffee,
  Moon,
  Play,
  User,
  UtensilsCrossed,
  type LucideIcon,
} from "lucide-react";
import "./clock-panel.css";
import { BREAK_TYPE_OPTIONS, type BreakTypeId } from "./derive";
import type { WorkTypeOption } from "./useWorkTypeOptions";

const BREAK_ICONS: Record<BreakTypeId, LucideIcon> = {
  lunch: UtensilsCrossed,
  coffee: Coffee,
  rest: Moon,
  personal: User,
};

export interface BreakPickerProps {
  onBack: () => void;
  onPick: (t: BreakTypeId) => void;
  /** pending 时全部禁点(动作管线防连点之外的 UI 层保险)。 */
  disabled?: boolean;
}

export function BreakPicker({ onBack, onPick, disabled }: BreakPickerProps) {
  const { t } = useTranslation("shifts");
  return (
    <div className="nvc-body">
      <button className="nvc-back" onClick={onBack} disabled={disabled}>
        <ChevronLeft size={11} />
        {t("attendanceClock.backBreakType")}
      </button>
      <div className="nvc-grid2">
        {BREAK_TYPE_OPTIONS.map((b) => {
          const Icon = BREAK_ICONS[b.id];
          return (
            <button
              key={b.id}
              className="nvc-chip"
              onClick={() => onPick(b.id)}
              disabled={disabled}
            >
              <Icon size={13} />
              {b.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface WorkTypePickerProps {
  options: WorkTypeOption[];
  loading: boolean;
  onBack: () => void;
  onStart: (code: string, label: string, notes: string) => void;
  disabled?: boolean;
}

export function WorkTypePicker({
  options,
  loading,
  onBack,
  onStart,
  disabled,
}: WorkTypePickerProps) {
  const { t } = useTranslation("shifts");
  const [sel, setSel] = useState<WorkTypeOption | null>(null);
  const [notes, setNotes] = useState("");
  // handoff 最终方案:仅"未选类型时 Start 禁用"(notes 可选;core.jsx:158 disabled={!sel})
  const canStart = !disabled && sel != null;
  return (
    <div className="nvc-body">
      <button className="nvc-back" onClick={onBack} disabled={disabled}>
        <ChevronLeft size={11} />
        {t("attendanceClock.backWorkType")}
      </button>
      {loading ? (
        <p className="nvc-hint">{t("attendanceClock.loadingWorkTypes")}</p>
      ) : (
        <div className="nvc-grid2">
          {options.map((w) => (
            <button
              key={w.code}
              className={"nvc-chip" + (sel?.code === w.code ? " sel" : "")}
              onClick={() => setSel(w)}
              disabled={disabled}
            >
              {w.label}
            </button>
          ))}
        </div>
      )}
      <textarea
        className="nvc-notes"
        rows={2}
        placeholder={t("attendanceClock.whatAreYouWorkingOn")}
        maxLength={500}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        disabled={disabled}
      />
      <div className="nvc-grid2" style={{ marginTop: 7 }}>
        <button
          className="nvc-btn primary full"
          disabled={!canStart}
          onClick={() => {
            // 建单标题用规范英文 label(显示用 label 是当前语言)
            if (sel) onStart(sel.code, sel.canonicalLabel ?? sel.label, notes.trim());
          }}
        >
          <Play size={10} fill="currentColor" />
          {t("attendanceClock.start")}
        </button>
      </div>
    </div>
  );
}

export interface ConfirmClockOutProps {
  onCancel: () => void;
  onConfirm: () => void;
  disabled?: boolean;
}

export function ConfirmClockOut({ onCancel, onConfirm, disabled }: ConfirmClockOutProps) {
  const { t } = useTranslation("shifts");
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, padding: "2px 2px 9px" }}>
        {t("attendanceClock.endYourShift")}
      </div>
      <div className="nvc-grid2">
        <button className="nvc-btn" onClick={onCancel} disabled={disabled}>
          {t("attendanceClock.cancel")}
        </button>
        <button className="nvc-btn danger" onClick={onConfirm} disabled={disabled}>
          {t("attendanceClock.clockOut")}
        </button>
      </div>
    </div>
  );
}
